import { SearchParamType } from "@/types/SearchParamType";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
/**
 * Custom hook to synchronize job key with URL search parameter 'jk'
 * Uses native History API to update URL without triggering React Router re-renders
 * @param selectedJobKey - Currently selected job key
 * @param setSelectedJobKey - Function to update selected job key
 * @param jobs - Array of jobs to validate job key exists
 */
export var useJobKeyUrlSync = function (selectedJobKey, setSelectedJobKey, jobs) {
    var searchParams = useSearchParams()[0];
    var isInitializedRef = useRef(false);
    var lastSelectedJobKeyRef = useRef(undefined);
    var containsJobKey = function (jobKey, jobs) {
        return jobs.some(function (job) { return job.jobKey === jobKey; });
    };
    // Initialize from URL on first load
    useEffect(function () {
        var _a;
        if (!isInitializedRef.current && jobs.length > 0) {
            var jkFromUrl = searchParams.get(SearchParamType.JOB_KEY);
            if (jkFromUrl && containsJobKey(jkFromUrl, jobs)) {
                // URL has valid job key - use it (handles page reloads)
                setSelectedJobKey(jkFromUrl);
            }
            else {
                // No valid URL job key - select first job
                setSelectedJobKey((_a = jobs[0]) === null || _a === void 0 ? void 0 : _a.jobKey);
            }
            isInitializedRef.current = true;
        }
    }, [jobs, searchParams, setSelectedJobKey]);
    // Handle job list changes after initialization
    useEffect(function () {
        var _a;
        if (isInitializedRef.current && jobs.length > 0) {
            var isSelectedJobInResults = selectedJobKey && containsJobKey(selectedJobKey, jobs);
            if (!isSelectedJobInResults) {
                // Current selection is not in results - select first job
                setSelectedJobKey((_a = jobs[0]) === null || _a === void 0 ? void 0 : _a.jobKey);
            }
        }
    }, [jobs, selectedJobKey, setSelectedJobKey]);
    // Update URL when selectedJobKey changes using native History API to avoid React re-renders
    useEffect(function () {
        if (isInitializedRef.current &&
            selectedJobKey !== lastSelectedJobKeyRef.current) {
            lastSelectedJobKeyRef.current = selectedJobKey;
            var currentUrl = new URL(window.location.href);
            var urlParams = new URLSearchParams(currentUrl.search);
            if (selectedJobKey) {
                urlParams.set(SearchParamType.JOB_KEY, selectedJobKey);
                // Update URL using native History API to avoid React Router re-renders
                var newUrl = "".concat(currentUrl.pathname, "?").concat(urlParams.toString()).concat(currentUrl.hash);
                window.history.replaceState(null, "", newUrl);
            }
        }
    }, [selectedJobKey]);
};
